import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { setTotalPrice } from "../../../redux/slice/bookingCreation/bookingCreationSlice";
import { formatDate } from "../../../util/date";
import moment from "moment";
import { formatCurrency } from "../../../util";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

interface BookingSummaryProps {
  roomImage: string;
  roomTitle: string;
  address: string;
  pricePerNight: number;
  dates: {
    start: string;
    end: string;
  };
  numberOfGuests: number;
  additionalServices: {
    id: string;
    assetId: string;
    serviceName: string;
    unitPrice: number;
    isPaymentCompulsory: boolean;
    quantity: boolean;
  }[];
}

// Utility function to count weekdays and weekends between two dates (inclusive start, exclusive end)
function countWeekdaysAndWeekends(start: moment.Moment, end: moment.Moment) {
  let weekdays = 0;
  let weekends = 0;
  let current = start.clone();
  while (current.isSameOrBefore(end, "day")) {
    const day = current.day(); // 0 = Sunday, 6 = Saturday
    if (day === 0 || day === 6) {
      weekends++;
    } else if (day <= 5) {
      // Friday counted as weekday
      weekdays++;
    }
    current.add(1, "day");
  }
  return { weekdays, weekends };
}

const BookingSummary: React.FC<BookingSummaryProps> = ({
  roomImage,
  roomTitle,
  address,
  pricePerNight,
  numberOfGuests,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const startDate = useSelector(
    (state: RootState) => state.bookingCreation.orderData.checkInDate
  );
  const endDate = useSelector(
    (state: RootState) => state.bookingCreation.orderData.checkOutDate
  );

  // Find weekday and weekend prices from priceScale (if available)
  const priceScale =
    useSelector(
      (state: RootState) =>
        state.apartment.apartmentDetails?.Group?.pricecategory?.pricescale
    ) || [];
  const weekdayPrice =
    priceScale.find((p: any) => p.days === "weekday")?.price ?? pricePerNight;
  const weekendPrice =
    priceScale.find((p: any) => p.days === "weekend")?.price ?? pricePerNight;
  const minPrice = Math.min(weekdayPrice, weekendPrice);

  // Calculate number of weekdays and weekends
  let weekdays = 0;
  let weekends = 0;
  if (startDate && endDate) {
    const counts = countWeekdaysAndWeekends(moment(startDate), moment(endDate));
    weekdays = counts.weekdays;
    weekends = counts.weekends;
  }

  // Compute total
  const priceDetails = [];
  let total = 0;
  if (weekdays > 0) {
    priceDetails.push({
      label: `${t("book_location.booking_summary.weekday")} (${weekdays}x)`,
      amount: weekdays * weekdayPrice,
    });
    total += weekdays * weekdayPrice;
  }
  if (weekends > 0) {
    priceDetails.push({
      label: `${t("book_location.booking_summary.weekend")} (${Math.ceil(
        weekends
      )}x)`,
      amount: Math.ceil(weekends / 3) * weekendPrice,
    });
    total += Math.ceil(weekends / 3) * weekendPrice;
  }

  useEffect(() => {
    dispatch(setTotalPrice(total));
  }, []);

  const [remarksOpen, setRemarksOpen] = useState(false);
  const [remarks, setRemarks] = useState("");

  return (
    <div className="max-w-[600px] mx-auto p-4 md:p-6">
      <div className="flex gap-4 mb-4 border-b border-gray-300 pb-2">
        <img
          src={roomImage}
          alt="Room"
          className="w-[100px] h-[100px] object-cover rounded-lg"
        />
        <div className="flex-1">
          <div className="font-medium text-base text-foreground mb-1">
            {roomTitle}
          </div>
          <div className="text-gray-500 text-xs mb-1">{address}</div>
        </div>
      </div>

      <div className="p-2 mb-1 border border-gray-300 rounded-md text-foreground">
        <div className="font-bold text-sm mb-2">
          {t("book_location.booking_summary.first_section_subtitle")}
        </div>
        <div className="flex justify-between text-sm mb-1">
          <span>{t("book_location.booking_summary.dates")}</span>
          <span>{`${formatDate(startDate)} - ${formatDate(endDate)}`}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span>{t("book_location.booking_summary.number_of_guests")}</span>
          <span>{numberOfGuests}</span>
        </div>
      </div>

      {/* Price Details Section */}
      <div className="p-2 mt-2 border border-gray-200 rounded-md text-foreground">
        <div className="font-bold text-sm mb-2">
          {t("book_location.booking_summary.price_details")}
        </div>
        <div className="space-y-2">
          {priceDetails.map((item, idx) => (
            <div className="flex justify-between text-sm" key={item.label}>
              <span>{item.label}</span>
              <span>{formatCurrency(item.amount, "de-DE")}</span>
            </div>
          ))}
          <div className="flex justify-between pt-3 font-bold text-sm border-t border-gray-200">
            <span>{t("book_location.booking_summary.total")}</span>
            <span>{formatCurrency(total, "de-DE")}</span>
          </div>
          <div className="text-gray-500 text-xs">
            {t("book_location.confirmation.tax_information")}
          </div>
        </div>
      </div>

      {/* Additional remarks section */}
      <div className="mt-4">
        <div
          className="flex items-center cursor-pointer select-none"
          onClick={() => setRemarksOpen((open) => !open)}
        >
          <span className="text-foreground font-bold text-sm mr-1">
            {t("book_location.venue_reservation_overview.additional_remarks")}
          </span>
          <span className="text-gray-400 text-sm font-normal mr-1">
            ({t("general.optional")})
          </span>
          <span className="text-foreground text-lg">
            {remarksOpen ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </span>
        </div>
        {remarksOpen && (
          <textarea
            className="w-full mt-2 p-2 border border-gray-300 rounded-md resize-none min-h-[60px] text-foreground"
            placeholder={t("general.remarks_placeholder")}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
          />
        )}
      </div>
    </div>
  );
};

export default BookingSummary;
